import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Wine, ShoppingCart, Heart, Check, Plus, ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Results() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [results, setResults] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const isPremium = user?.is_premium || false;

  useEffect(() => {
    // Load results from session storage
    const storedResults = sessionStorage.getItem('scanResults');
    if (storedResults) {
      setResults(JSON.parse(storedResults));
    }
    
    // Load existing favorites
    const loadFavorites = async () => {
      try {
        const response = await axios.get(`${API}/favorites`, { headers: getAuthHeaders() });
        setFavorites(new Set(response.data.map(f => f.cocktail_id)));
      } catch (error) {
        console.error('Error loading favorites:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFavorites();
  }, [getAuthHeaders]);

  const toggleFavorite = async (cocktailId) => {
    if (!isPremium) {
      toast.error('Favorites are a premium feature!', {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/pricing')
        }
      });
      return;
    }
    
    try {
      if (favorites.has(cocktailId)) {
        await axios.delete(`${API}/favorites/${cocktailId}`, { headers: getAuthHeaders() });
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(cocktailId);
          return next;
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { cocktail_id: cocktailId }, { headers: getAuthHeaders() });
        setFavorites(prev => new Set(prev).add(cocktailId));
        toast.success('Added to favorites!');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const addToShoppingList = async (item) => {
    try {
      await axios.post(`${API}/shopping-list`, { item_name: item }, { headers: getAuthHeaders() });
      toast.success(`Added ${item} to shopping list`);
    } catch (error) {
      toast.error('Failed to add to shopping list');
    }
  };

  if (!results) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <Wine className="w-16 h-16 text-[#525252] mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-[#F5F5F5] mb-4">No Results Found</h1>
        <p className="text-[#A3A3A3] mb-8">Scan your bar first to see cocktail matches</p>
        <Button
          onClick={() => navigate('/scan')}
          className="bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full px-8 py-6 btn-press"
        >
          Go to Scan
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="results-page">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-3">
          Your Results
        </h1>
        <p className="text-[#A3A3A3] text-lg">
          We found {results.detected_bottles?.length || 0} items and {results.matched_cocktails?.length || 0} cocktails you can make
        </p>
      </div>

      {/* Detected Bottles */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-[#F5F5F5] mb-4 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#D4AF37]" />
          Detected in Your Bar
        </h2>
        <div className="flex flex-wrap gap-2">
          {results.detected_bottles?.map((bottle, index) => (
            <span
              key={index}
              className="ingredient-tag capitalize animate-fadeIn"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <Check className="w-3 h-3 mr-1" />
              {bottle}
            </span>
          ))}
        </div>
      </div>

      {/* Matched Cocktails */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-[#F5F5F5] mb-6">
          Cocktails You Can Make
        </h2>
        
        {results.matched_cocktails?.length === 0 ? (
          <div className="empty-state glass rounded-2xl">
            <Wine className="w-12 h-12 text-[#525252] mb-4" />
            <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">No matches found</h3>
            <p className="text-[#A3A3A3]">Check out our shopping suggestions below</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.matched_cocktails?.map((cocktail, index) => (
              <div
                key={cocktail.id}
                className="cocktail-card animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={cocktail.image_url}
                    alt={cocktail.name}
                    className="cocktail-card-image"
                  />
                  <button
                    onClick={() => toggleFavorite(cocktail.id)}
                    className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      favorites.has(cocktail.id)
                        ? 'bg-[#EF4444] text-white'
                        : 'bg-black/40 text-white/70 hover:text-white'
                    }`}
                    data-testid={`favorite-btn-${cocktail.id}`}
                  >
                    <Heart className={`w-5 h-5 ${favorites.has(cocktail.id) ? 'fill-current' : ''}`} />
                  </button>
                </div>
                
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-[#F5F5F5]">{cocktail.name}</h3>
                    <span className="category-badge">{cocktail.category}</span>
                  </div>
                  
                  {/* Ingredient Breakdown */}
                  <div className="space-y-2 mb-4">
                    {/* Detected ingredients */}
                    {cocktail.detected_ingredients?.length > 0 && (
                      <div>
                        <span className="text-xs text-[#10B981] font-medium flex items-center gap-1 mb-1">
                          <Check className="w-3 h-3" /> You have:
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {cocktail.detected_ingredients.map((ing, i) => (
                            <span key={i} className="text-xs bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full">
                              {ing}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Basics needed */}
                    {cocktail.basics_needed?.length > 0 && (
                      <div>
                        <span className="text-xs text-[#F59E0B] font-medium flex items-center gap-1 mb-1">
                          <AlertCircle className="w-3 h-3" /> Basics needed:
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {cocktail.basics_needed.map((ing, i) => (
                            <span key={i} className="text-xs bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-0.5 rounded-full">
                              {ing}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => navigate(`/cocktails/${cocktail.id}`)}
                    variant="outline"
                    className="w-full border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full btn-press"
                    data-testid={`view-recipe-btn-${cocktail.id}`}
                  >
                    View Recipe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Almost Can Make Section */}
      {results.almost_can_make?.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-[#F5F5F5] mb-4 flex items-center gap-2">
            <Wine className="w-6 h-6 text-[#F59E0B]" />
            Almost Can Make
          </h2>
          <p className="text-[#A3A3A3] mb-6">
            Just missing a few ingredients
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.almost_can_make.map((item, index) => (
              <div
                key={item.cocktail.id}
                className="bg-[#121212] border border-white/5 rounded-2xl p-5 animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex gap-4">
                  <img
                    src={item.cocktail.image_url}
                    alt={item.cocktail.name}
                    className="w-20 h-20 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-[#F5F5F5] font-bold mb-1">{item.cocktail.name}</h3>
                    <div className="text-xs text-[#EF4444] mb-2">
                      Missing: {item.missing_ingredients?.join(', ')}
                    </div>
                    {item.basics_needed?.length > 0 && (
                      <div className="text-xs text-[#F59E0B]">
                        Basics: {item.basics_needed.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shopping Suggestions */}
      {results.suggestions?.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-[#F5F5F5] mb-4 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-[#10B981]" />
            Smart Shopping Suggestions
          </h2>
          <p className="text-[#A3A3A3] mb-6">
            Buy these items to unlock more cocktails
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.suggestions.map((suggestion, index) => (
              <div
                key={suggestion.ingredient}
                className="bg-[#121212] border border-white/5 rounded-2xl p-5 card-lift animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-[#F5F5F5] font-bold capitalize mb-1">
                      {suggestion.ingredient}
                    </h3>
                    <p className="text-[#10B981] text-sm">
                      Unlocks {suggestion.unlocks_cocktails} cocktail{suggestion.unlocks_cocktails !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <Button
                    onClick={() => addToShoppingList(suggestion.ingredient)}
                    size="sm"
                    className="bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 rounded-full btn-press"
                    data-testid={`add-shopping-btn-${index}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={() => navigate('/scan')}
          className="flex-1 bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full py-6 gold-glow btn-press"
        >
          Scan Again
        </Button>
        <Button
          onClick={() => navigate('/cocktails')}
          variant="outline"
          className="flex-1 border-white/20 text-[#A3A3A3] hover:text-[#F5F5F5] rounded-full py-6 btn-press"
        >
          Browse All Cocktails
        </Button>
      </div>
    </div>
  );
}
